import { set } from 'lodash-es';
import { type PropsWithChildren, useCallback, useState } from 'react';

import type { CustomEvent } from '@/util';
import { createSafeContext, uuid } from '@/util';

import type { VBaseInputProps } from '../../VBaseInput';
import type { VInputType, VOriginInputProps } from '../../VInput/type';
import { VModal } from '../../VModal';
import VPromptModalMessage from './VPromptModalMessage';
import type {
  VModalContextValue,
  VModalOpenAlertOption,
  VModalOpenConfirmOption,
  VModalOpenOption,
  VModalOption,
} from './type';

const DEFAULT_MODAL_DURATION = 500;

export const VModalContext = createSafeContext<VModalContextValue>();

export const VModalContextProvider = ({ children }: PropsWithChildren) => {
  const [modalOptionById, setModalOptionById] = useState<{
    [id: string]: VModalOption;
  }>({});
  const modalIdList = Object.keys(modalOptionById);

  const close = (id: string) => {
    setModalOptionById(prev =>
      Object.entries(prev).reduce(
        (acc, [_id, option]) => ({
          ...acc,
          [_id]:
            id !== _id
              ? option
              : {
                  ...option,
                  visible: false,
                },
        }),
        {} as {
          [id: string]: VModalOption;
        },
      ),
    );
  };
  const closeAll = () => {
    setModalOptionById(prev =>
      Object.entries(prev).reduce(
        (acc, [id, option]) => ({
          ...acc,
          [id]: {
            ...option,
            visible: false,
          },
        }),
        {} as {
          [id: string]: VModalOption;
        },
      ),
    );
  };

  const openAlert = useCallback(
    (option: VModalOpenAlertOption<'default'>) => {
      return open({
        confirmText: 'Confirm',
        ...option,
        type: 'default',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const openConfirm = useCallback(
    (option: VModalOpenConfirmOption<'default'>) => {
      return open({
        cancelText: 'Cancel',
        confirmText: 'Confirm',
        ...option,
        type: 'default',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const openDangerAlert = useCallback(
    (option: VModalOpenAlertOption<'danger'>) => {
      return open({
        confirmText: 'Confirm',
        ...option,
        type: 'danger',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const openDangerConfirm = useCallback(
    (option: VModalOpenConfirmOption<'danger'>) => {
      return open({
        cancelText: 'Cancel',
        confirmText: 'Confirm',
        ...option,
        type: 'danger',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const openPrompt = useCallback(
    <T extends VInputType>(
      option: Omit<
        VModalOpenConfirmOption<'default'>,
        'type' | 'message' | 'onConfirm'
      > & {
        inputProps?: VOriginInputProps<T> & {
          defaultValue?: string;
        } & VBaseInputProps;
        promptMessage?: string | (() => React.ReactNode);
        onConfirm?: (
          event: CustomEvent<'confirm', null, { value: string }>,
        ) => any;
      },
    ) => {
      const { inputProps = {}, promptMessage, onConfirm } = option;
      let promptValue = inputProps?.defaultValue ?? '';
      let validationError: Nullish<Error>;

      return open({
        cancelText: 'Cancel',
        confirmText: 'Confirm',
        ...option,
        onConfirm: e => {
          set(e, '_target', {
            value: promptValue,
          });

          try {
            const result = onConfirm?.(e);

            if (result instanceof Promise) {
              result.catch(error => {
                validationError =
                  error instanceof Error ? error : new Error(error);

                setModalOptionById(prev => ({ ...prev }));
              });
            }

            return result;
          } catch (error: any) {
            validationError = error instanceof Error ? error : new Error(error);

            setModalOptionById(prev => ({ ...prev }));
            throw error;
          }
        },
        message: () => {
          return (
            <VPromptModalMessage
              inputProps={inputProps}
              promptMessage={promptMessage}
              validationError={validationError}
              onChangePromptValue={value => (promptValue = value)}
            />
          );
        },
        type: 'default',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const open = useCallback(
    <T extends 'default' | 'danger'>(option: VModalOpenOption<T>) => {
      const id = option.id ?? `v-confirm-modal-${uuid()}`;

      option.id ??= id;
      option.duration ??= DEFAULT_MODAL_DURATION;
      option.type === 'danger' && (option.confirmVariant = 'danger');

      setModalOptionById(prev => ({
        ...prev,
        [id]: {
          noCloseOnBackdrop: false,
          noCloseOnEsc: false,
          ...option,
          visible: true,
          onHidden(event) {
            removeModal(id);
            option.onHidden?.(event);
          },
        } as VModalOption,
      }));

      return id;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  const removeModal = useCallback(
    (id: string) => {
      setModalOptionById(prev => {
        delete prev[id];

        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOptionById],
  );

  return (
    <VModalContext.Provider
      value={{
        close,
        closeAll,
        openAlert,
        openConfirm,
        openDangerAlert,
        openDangerConfirm,
        openPrompt,
      }}
    >
      {children}

      {modalIdList.map(id => {
        const option = modalOptionById[id];
        const {
          cancelVariant,
          cancelText,
          confirmVariant,
          confirmText,
          message,
          title,
          type,
          onCancel,
          onConfirm,
          ...restOption
        } = option;

        return (
          <VModal
            {...restOption}
            key={id}
            id={id}
            footer={{
              cancelText,
              cancelVariant,
              confirmText,
              confirmVariant,
              onCancel,
              onConfirm,
            }}
            title={() =>
              title && (
                <span
                  className={(type as any) === 'danger' ? 'text-error-500' : ''}
                >
                  {typeof title === 'string' ? title : title()}
                </span>
              )
            }
            onUpdateVisible={visible => {
              !visible && close(id);
              restOption.onUpdateVisible?.(visible);
            }}
          >
            {message &&
              (typeof message === 'string' ? <div>{message}</div> : message())}
          </VModal>
        );
      })}
    </VModalContext.Provider>
  );
};
